import { TradeTapeItem as DrfqTradeTapeItem } from '@paradigm/features/src/unified-rfqs/entities/drfq/trades';
import { MergedSourcesFetcher } from '@paradigm/features/src/unified-rfqs/services/merged-sources-fetcher';
import { ListTradesReq } from '@paradigm/features/src/unified-rfqs/repositories/grfq-types';
import {
  AuctionType,
  TradeTapeItem,
} from '@paradigm/features/src/unified-rfqs/entities/shared';
import TradeTapeStore from '@paradigm/features/src/unified-rfqs/store/trade-tape';
import { FormOption } from '@paradigm/features/src/unified-rfqs/ui/rfq-builder/types';
import { ProductCode } from '#/products/types';
import { GrfqTradeTape } from '@paradigm/features/src/unified-rfqs/entities/grfq/domain';

import PublicStore from '../store/public-store';
import { GrfqService } from './grfq';
import { DrfqService } from './drfq';

export interface FiltersDeps {
  auctionTypes: FormOption<AuctionType>[];
  productCodes: ProductCode[];
  strategyCodes: string[];
}

interface TradeTapeServiceDeps {
  grfqService: GrfqService;
  drfqService: DrfqService;
  tradeTapeStore: TradeTapeStore;
  fetcher: MergedSourcesFetcher<ListTradesReq, TradeTapeItem>;
  filters: FiltersDeps;
}

type InstanceDeps = Omit<TradeTapeServiceDeps, 'fetcher'>;

const PAGE_SIZE = 50;

export default class UnifiedTradeTapeService extends PublicStore<TradeTapeItem> {
  readonly fetcher;
  public filters;
  constructor(deps: TradeTapeServiceDeps) {
    super(deps.tradeTapeStore);
    this.fetcher = deps.fetcher;
    this.filters = deps.filters;
  }

  static build(deps: InstanceDeps): UnifiedTradeTapeService {
    const { grfqService, drfqService, tradeTapeStore } = deps;

    const { filters } = deps;

    const filterTradeTape =
      (filtersDeps: FiltersDeps) =>
      (trade: TradeTapeItem): boolean => {
        const validActionType = hasAuctionTypes(
          trade,
          filtersDeps.auctionTypes,
        );
        const validProduct = hasProduct(trade, filtersDeps.productCodes);
        const validStrategy = hasStrategy(trade, filtersDeps.strategyCodes);

        const valid = validActionType && validProduct && validStrategy;

        return valid;
      };

    const fetchers = [];

    if (shouldAddFetcher('OB', filters))
      fetchers.push(deps.grfqService.listTradeTape);

    if (shouldAddFetcher('RFQ', filters))
      fetchers.push(deps.drfqService.listTradeTape);

    const fetcher = new MergedSourcesFetcher<ListTradesReq, TradeTapeItem>({
      pageSize: PAGE_SIZE,
      fetchers,
      store: tradeTapeStore,
      filterFn: filterTradeTape(filters),
    });

    return new UnifiedTradeTapeService({
      grfqService,
      drfqService,
      tradeTapeStore,
      fetcher,
      filters,
    });
  }
}

export function buildTradeTapeService(deps: InstanceDeps) {
  return UnifiedTradeTapeService.build(deps);
}

const isGrfqTrade = (trade: TradeTapeItem): trade is GrfqTradeTape =>
  trade.rfqType === 'grfq';

export const hasProduct = (
  trade: TradeTapeItem,
  productCodeOptions: readonly string[],
): boolean => {
  const isProductCodeEmpty = productCodeOptions.length === 0;
  if (isProductCodeEmpty) return true;

  const grfqHaveProduct = (grfqTrade: GrfqTradeTape) =>
    grfqTrade.product_codes.some((code: string) =>
      productCodeOptions.includes(code),
    );

  const drfqHaveProduct = (drfqTrade: DrfqTradeTapeItem) =>
    drfqTrade.legs.some((leg) => productCodeOptions.includes(leg.product_code));

  return isGrfqTrade(trade) ? grfqHaveProduct(trade) : drfqHaveProduct(trade);
};

export const hasStrategy = (
  trade: TradeTapeItem,
  strategyCodeOptions: readonly string[],
): boolean => {
  const isStrategyCodeEmpty = strategyCodeOptions.length === 0;

  if (isStrategyCodeEmpty) return true;

  return strategyCodeOptions.includes(trade.strategy_code);
};

export const hasAuctionTypes = (
  trade: TradeTapeItem,
  auctionTypesOptions: readonly FormOption<AuctionType>[],
): boolean => {
  const isAuctionTypeEmpty = auctionTypesOptions.length === 0;

  if (isAuctionTypeEmpty) return true;

  const grfqHaveAuctionType = () =>
    auctionTypesOptions.some((auctionType) => 'OB' === auctionType.value);

  const drfqHaveAuctionType = () =>
    auctionTypesOptions.some((auctionType) => 'RFQ' === auctionType.value);

  return isGrfqTrade(trade) ? grfqHaveAuctionType() : drfqHaveAuctionType();
};

const shouldAddFetcher = (type: AuctionType, filters: FiltersDeps) =>
  filters.auctionTypes.some((option) => option.value === type) ||
  filters.auctionTypes.length === 0;
